import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainContainer from "../../components/MainContainer";

import {
  BackButton,
  Button,
  CharCount,
  Container,
  Content,
  Input,
  InputContainer,
  Inputs,
  Label,
  ProfileImage,
  ProfileImageContainer,
  Textarea,
  Title,
  UploadPhotoButton,
  Wrapper,
  DeleteIcon,
} from "./EditProfilePage.styles";
import {
  EmptyProfile,
  EmptyText,
  Button as EmptyStateButton,
} from "../ProfilePage/ProfilePage.styles";

import DefaultProfilePhoto from "../../components/DefaultProfilePhoto/DefaultProfilePhoto";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import deleteAnimation from "../../assets/animations/trash.json";
import loaderAnimation from "../../assets/animations/onLoad.json";

import { toast } from "sonner";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import CropModal from "../../components/CropModal/CropModal";
import {
  selectUser,
  selectUpdateProfilePending,
} from "../../store/slices/user/slice";
import {
  selectCategories,
  setSelectedExploreCollectionFilter,
} from "../../store/slices/model/slice";
import { updateProfile } from "../../store/slices/user/asyncThunk";

const EditProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const user = useSelector(selectUser);
  const updateProfilePending = useSelector(selectUpdateProfilePending);
  const { data: categories } = useSelector(selectCategories);

  const userNotFound = !!user && Number(user?.activeModels ?? 0) === 0;

  const [username, setUsername] = useState(user?.username ?? "");
  const [bio, setBio] = useState(user?.bio ?? "");
  const [profilePhoto, setProfilePhoto] = useState(user?.profilePhoto ?? null);
  const [photoFile, setPhotoFile] = useState(null);
  const [cropSource, setCropSource] = useState(null);

  useEffect(() => {
    if (!user) return;
    setUsername(user.username ?? "");
    setBio(user.bio ?? "");
    setProfilePhoto(user.profilePhoto ?? null);
  }, [user]);

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image is too large. Maximum size is 20MB");
      return;
    }

    const url = URL.createObjectURL(file);
    setCropSource({ url, file });
    e.target.value = "";
  };

  const handleCropConfirm = ({ blob, url }) => {
    if (!blob) return;
    if (cropSource?.url) URL.revokeObjectURL(cropSource.url);
    const croppedFile = new File([blob], `avatar-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
    setPhotoFile(croppedFile);
    setProfilePhoto(url);
    setCropSource(null);
  };

  const handleCropClose = () => {
    if (cropSource?.url) URL.revokeObjectURL(cropSource.url);
    setCropSource(null);
  };

  const bioContainsLink =
    /(https?:\/\/|www\.|[a-z0-9-]+\.(com|net|org|io|co|me|tv|gg|app|xyz|info|biz|ru|us|uk|de|fr|es|it|nl|pl|cz|jp|cn|tk|ml|ga|cf|ly|to|st|am|fm|tm|cc|sh|ai|dev|live|link|site|online|store|shop|tech|club|world|space|design|art|news|blog)\b|t\.me\/|telegram:|discord\.gg|onlyfans\.com)/i.test(
      bio,
    );

  const handleSave = async () => {
    if (bioContainsLink) {
      toast.error("Links are not allowed in bio");
      return;
    }

    dispatch(
      updateProfile({
        username,
        bio,
        image: photoFile,
      }),
    )
      .unwrap()
      .then(() => {
        toast.success("Profile updated successfully");
        navigate(-1);
      })
      .catch((err) => {
        toast.error(err.message || "Failed to update profile");
      });
  };
  if (userNotFound) {
    return (
      <MainContainer hideTabs fullWidth>
        <EmptyProfile>
          <LottieIcon
            animationData={loaderAnimation}
            trigger="loop"
            size={180}
            style={{ marginTop: "-75px" }}
          />
          <EmptyText>No luck here!</EmptyText>
          <EmptyText $light>Explore templates instead.</EmptyText>
          <EmptyStateButton
            onClick={() => {
              navigate("/");
              if (categories?.[0]?._id) {
                dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
              }
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }}
          >
            Visit Explore
          </EmptyStateButton>
        </EmptyProfile>
      </MainContainer>
    );
  }

  return (
    <MainContainer hideTabs fullWidth>
      <BackButton onClick={() => navigate(-1)}>
        <ChevronSvg />
        Back to Profile
      </BackButton>
      <Container>
        <Title>Edit Profile</Title>
        <Content>
          <ProfileImageContainer>
            {profilePhoto ? (
              <ProfileImage src={profilePhoto} alt="Profile" />
            ) : (
              <DefaultProfilePhoto size={110} />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <UploadPhotoButton onClick={handlePhotoClick}>
              {profilePhoto ? "Change Photo" : "Upload Photo"}
            </UploadPhotoButton>
          </ProfileImageContainer>

          <Inputs>
            <InputContainer>
              <Label>Username</Label>
              <Input
                value={username}
                placeholder="Enter your username"
                onChange={(e) => setUsername(e.target.value)}
              />
            </InputContainer>
            <InputContainer>
              <Label>Bio</Label>
              <Textarea
                value={bio}
                placeholder="Tell something about yourself..."
                maxLength={200}
                onChange={(e) => setBio(e.target.value)}
              />
              <CharCount>{bio.length}/200</CharCount>
              {bioContainsLink && (
                <CharCount style={{ color: "#FF474A" }}>
                  Links are not allowed in bio.
                </CharCount>
              )}
            </InputContainer>
            <InputContainer>
              <Label>Danger Zone</Label>
              <Wrapper>
                Delete Account
                <DeleteIcon
                  onClick={() => {
                    navigate("/account/delete");
                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: "instant",
                    });
                  }}
                >
                  <LottieIcon
                    animationData={deleteAnimation}
                    trigger="hover"
                    size={24}
                    style={{ cursor: "pointer" }}
                    strokeColor={"#FF474A"}
                  />
                </DeleteIcon>
              </Wrapper>
            </InputContainer>
          </Inputs>

          <Button
            onClick={handleSave}
            disabled={updateProfilePending || bioContainsLink}
          >
            {updateProfilePending ? "Saving..." : "Save"}
          </Button>
        </Content>
      </Container>

      <CropModal
        isOpen={!!cropSource}
        onClose={handleCropClose}
        onConfirm={handleCropConfirm}
        selectedImage={cropSource}
        title="Crop Profile Photo"
        lockedAspect={1}
        circlePreview
      />
    </MainContainer>
  );
};

export default EditProfilePage;
